import { useState, useEffect } from 'react';
import {
  GitCompare,
  Sparkles,
  Check,
  X,
  RotateCcw,
  Loader2,
  Layers,
  Globe,
  ExternalLink,
  AlertTriangle,
  Info,
  ChevronDown,
  Brain,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { api } from '@/services/client';
import { PageLayout } from '@/components/layout/PageLayout';

interface Session {
  id: string;
  name?: string;
  created_at?: string;
}

interface Tab {
  id: string;
  title: string;
  url: string;
  domain?: string;
  favicon?: string;
}

interface ComparisonResult {
  title?: string;
  summary?: string;
  tabs?: number;
  criteria?: string[];
}

const PREDEFINED_CRITERIA = [
  'Content quality',
  'Relevance to research',
  'Source credibility',
  'Depth of information',
  'Recency / Freshness',
  'Design & usability',
  'Features & functionality',
  'Value for money',
  'Audience fit',
  'Technical accuracy',
];

export function ComparisonBuilder() {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
  const [tabs, setTabs] = useState<Tab[]>([]);
  const [selectedTabIds, setSelectedTabIds] = useState<Set<string>>(new Set());
  const [criteria, setCriteria] = useState<Set<string>>(new Set());
  const [customCriteriaInput, setCustomCriteriaInput] = useState('');
  const [comparisonResult, setComparisonResult] = useState<ComparisonResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingTabs, setIsLoadingTabs] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSessions();
  }, []);

  useEffect(() => {
    if (selectedSessionId) {
      loadTabs(selectedSessionId);
    }
  }, [selectedSessionId]);

  const loadSessions = async () => {
    try {
      const response = await api.get('/sessions');
      const sessionsData = response.data?.sessions || [];
      setSessions(sessionsData);
      if (sessionsData.length > 0) {
        setSelectedSessionId(sessionsData[0].id);
      }
    } catch (err) {
      console.error('Failed to load sessions:', err);
    }
  };

  const loadTabs = async (sessionId: string) => {
    setIsLoadingTabs(true);
    setSelectedTabIds(new Set());
    setComparisonResult(null);
    setError(null);
    try {
      const response = await api.get(`/sessions/${sessionId}/tabs`);
      setTabs(response.data?.tabs || []);
    } catch (err) {
      console.error('Failed to load tabs:', err);
      setTabs([]);
    } finally {
      setIsLoadingTabs(false);
    }
  };

  const toggleTab = (tabId: string) => {
    setSelectedTabIds((prev) => {
      const next = new Set(prev);
      if (next.has(tabId)) {
        next.delete(tabId);
      } else {
        next.add(tabId);
      }
      return next;
    });
  };

  const toggleCriteria = (item: string) => {
    setCriteria((prev) => {
      const next = new Set(prev);
      if (next.has(item)) {
        next.delete(item);
      } else {
        next.add(item);
      }
      return next;
    });
  };

  const addCustomCriteria = () => {
    const trimmed = customCriteriaInput.trim();
    if (trimmed && !criteria.has(trimmed)) {
      setCriteria((prev) => new Set([...prev, trimmed]));
      setCustomCriteriaInput('');
    }
  };

  const removeCriteria = (item: string) => {
    setCriteria((prev) => {
      const next = new Set(prev);
      next.delete(item);
      return next;
    });
  };

  const handleCompare = async () => {
    if (selectedTabIds.size < 2) {
      setError('Select at least 2 tabs to compare.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setComparisonResult(null);

    try {
      const response = await api.post('/llm/compare', {
        tab_ids: Array.from(selectedTabIds),
        criteria: criteria.size > 0 ? Array.from(criteria) : undefined,
      });
      setComparisonResult(response.data);
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Failed to run comparison';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setSelectedTabIds(new Set());
    setCriteria(new Set());
    setCustomCriteriaInput('');
    setComparisonResult(null);
    setError(null);
  };

  const selectedTabs = tabs.filter((t) => selectedTabIds.has(t.id));

  return (
    <PageLayout
      title="Comparison Builder"
      description="Compare browser tab content side-by-side using AI"
      icon={GitCompare}
    >
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="p-4 bg-primary-500/20 rounded-2xl border border-primary-500/30">
              <GitCompare className="w-10 h-10 text-primary-400" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-khiimori-text-primary">Comparison Builder</h2>
              <p className="text-khiimori-text-muted mt-1">
                AI-powered side-by-side comparison of tab content
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="glass"
              size="md"
              onClick={handleClear}
              disabled={selectedTabIds.size === 0 && !comparisonResult}
            >
              <RotateCcw className="w-4 h-4" />
              Clear
            </Button>
          </div>
        </div>
      </div>

      {/* Session selector */}
      {sessions.length > 0 && (
        <div className="mb-5">
          <label className="block text-sm font-medium text-khiimori-text-muted mb-2">
            Browser Session
          </label>
          <div className="relative inline-block w-full max-w-xs">
            <select
              value={selectedSessionId ?? ''}
              onChange={(e) => setSelectedSessionId(e.target.value || null)}
              className="w-full appearance-none bg-khiimori-bg-elevated/10 border border-khiimori-border/15 rounded-xl px-4 py-3 text-khiimori-text-primary text-sm pr-10 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500/50"
            >
              {sessions.map((s) => (
                <option key={s.id} value={s.id} className="bg-khiimori-bg-elevated">
                  Session {s.id.slice(0, 8)}...
                  {s.name ? ` - ${s.name}` : ''}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-khiimori-text-muted pointer-events-none" />
          </div>
        </div>
      )}

      {/* Step 1: Select tabs */}
      <Card
        padding="lg"
        className="mb-5 bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10"
      >
        <div className="flex items-center gap-2 mb-4">
          <Layers className="w-5 h-5 text-primary-400" />
          <h2 className="text-lg font-semibold text-khiimori-text-primary">
            Select tabs to compare
          </h2>
          {selectedTabIds.size > 0 && (
            <Badge variant="primary" size="sm">
              {selectedTabIds.size} selected
            </Badge>
          )}
        </div>

        {isLoadingTabs ? (
          <div className="flex items-center gap-2 text-khiimori-text-muted py-8 justify-center">
            <Loader2 className="w-5 h-5 animate-spin" />
            Loading tabs...
          </div>
        ) : tabs.length === 0 ? (
          <div className="text-center py-8 text-khiimori-text-muted">
            <Globe className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>No tabs found in this session.</p>
            <p className="text-sm mt-1">
              Open some browser tabs to start comparing.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto pr-1">
            {tabs.map((tab) => {
              const isSelected = selectedTabIds.has(tab.id);
              return (
                <button
                  key={tab.id}
                  onClick={() => toggleTab(tab.id)}
                  className={`flex items-start gap-2 p-3 rounded-xl border text-left transition-all ${
                    isSelected
                      ? 'bg-primary-500/15 border-primary-500/40 ring-2 ring-primary-500/30'
                      : 'bg-khiimori-bg-elevated/5 border-khiimori-border/10 hover:bg-khiimori-bg-elevated/10'
                  }`}
                >
                  <div
                    className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                      isSelected
                        ? 'bg-primary-500 border-primary-500'
                        : 'border-primary-500'
                    }`}
                  >
                    {isSelected && <Check className="w-3 h-3 text-khiimori-text-primary" />}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-khiimori-text-primary truncate">
                      {tab.title || 'Untitled'}
                    </p>
                    <p className="text-xs text-khiimori-text-muted truncate flex items-center gap-1">
                      <Globe className="w-3 h-3 flex-shrink-0" />
                      {tab.url
                        ? new URL(tab.url).hostname
                        : 'No URL'}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </Card>

      {/* Step 2: Criteria */}
      <Card
        padding="lg"
        className="mb-5 bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10"
      >
        <div className="flex items-center gap-2 mb-4">
          <Brain className="w-5 h-5 text-primary-400" />
          <h2 className="text-lg font-semibold text-khiimori-text-primary">
            Comparison criteria (optional)
          </h2>
        </div>

        {/* Selected criteria chips */}
        {criteria.size > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {Array.from(criteria).map((c) => (
              <Badge key={c} variant="primary" size="md" className="flex items-center gap-1">
                {c}
                <button onClick={() => removeCriteria(c)} className="ml-1 hover:opacity-70">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}

        {/* Predefined criteria */}
        <div className="flex flex-wrap gap-2 mb-4">
          {PREDEFINED_CRITERIA.filter((c) => !criteria.has(c)).map((c) => (
            <button
              key={c}
              onClick={() => toggleCriteria(c)}
              className="px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors bg-khiimori-bg-elevated/5 border-khiimori-border/15 text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/15 hover:text-khiimori-text-primary"
            >
              + {c}
            </button>
          ))}
        </div>

        {/* Custom criteria input */}
        <div className="flex gap-2">
          <input
            type="text"
            value={customCriteriaInput}
            onChange={(e) => setCustomCriteriaInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addCustomCriteria();
              }
            }}
            placeholder="Type custom criteria and press Enter..."
            className="flex-1 bg-khiimori-bg-elevated/10 border border-khiimori-border/15 rounded-xl px-4 py-2 text-sm text-khiimori-text-primary placeholder:text-khiimori-text-muted focus:outline-none focus:ring-2 focus:ring-primary-500/50"
          />
          <Button
            variant="secondary"
            size="sm"
            onClick={addCustomCriteria}
            disabled={!customCriteriaInput.trim()}
          >
            Add
          </Button>
        </div>
      </Card>

      {/* Compare button */}
      <div className="flex items-center gap-3 mb-6">
        <Button
          variant="gradient"
          size="lg"
          onClick={handleCompare}
          isLoading={isLoading}
          disabled={selectedTabIds.size < 2 || isLoading}
        >
          <Sparkles className="w-5 h-5" />
          Compare {selectedTabIds.size} tab{selectedTabIds.size !== 1 ? 's' : ''}
        </Button>
        {selectedTabIds.size < 2 && (
          <span className="text-sm text-khiimori-text-muted flex items-center gap-1">
            <Info className="w-4 h-4" />
            Select at least 2 tabs to enable comparison
          </span>
        )}
      </div>

      {/* Error */}
      {error && (
        <Card
          padding="md"
          className="mb-6 border border-warning-500/30 bg-warning-500/10"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-warning-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-warning-300">Comparison failed</p>
              <p className="text-sm text-warning-400/80 mt-0.5">{error}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Loading indicator */}
      {isLoading && (
        <Card
          padding="xl"
          className="mb-6 bg-khiimori-bg-elevated/5 border border-khiimori-border/10 text-center"
        >
          <Loader2 className="w-8 h-8 animate-spin text-primary-400 mx-auto mb-3" />
          <p className="text-khiimori-text-secondary font-medium">
            AI is analyzing tab content...
          </p>
          <p className="text-sm text-khiimori-text-muted mt-1">
            Comparing {selectedTabIds.size} tabs
            {criteria.size > 0 && ` across ${criteria.size} criteria`}
          </p>
        </Card>
      )}

      {/* Results */}
      {comparisonResult && (
        <>
          {/* Summary Panel */}
          <Card
            padding="lg"
            className="mb-6 bg-gradient-primary border border-primary-500/20"
          >
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="w-6 h-6 text-khiimori-text-primary" />
              <h2 className="text-2xl font-bold text-khiimori-text-primary">
                {comparisonResult.title || 'Comparison Results'}
              </h2>
            </div>

            {/* Summary text */}
            <div className="mb-4">
              <p className="text-khiimori-text-primary/90 whitespace-pre-wrap leading-relaxed">
                {comparisonResult.summary || 'No summary available.'}
              </p>
            </div>

            {/* Meta */}
            <div className="flex flex-wrap gap-3">
              <Badge variant="primary" size="sm">
                {comparisonResult.tabs} tabs compared
              </Badge>
              {comparisonResult.criteria?.map((c: string) => (
                <Badge key={c} variant="default" size="sm">
                  {c}
                </Badge>
              ))}
            </div>
          </Card>

          {/* Compared tabs listing */}
          <Card
            padding="lg"
            className="mb-6 bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10"
          >
            <h3 className="text-lg font-semibold text-khiimori-text-primary mb-3">
              Compared tabs
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {selectedTabs.map((tab) => (
                <a
                  key={tab.id}
                  href={tab.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 rounded-xl border border-khiimori-border/10 bg-khiimori-bg-elevated/5 hover:bg-khiimori-bg-elevated/10 transition-colors group"
                >
                  <Globe className="w-4 h-4 text-khiimori-text-muted group-hover:text-primary-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-khiimori-text-primary truncate group-hover:text-primary-300 transition-colors">
                      {tab.title || 'Untitled'}
                    </p>
                    <p className="text-xs text-khiimori-text-muted truncate">
                      {tab.domain || (tab.url ? new URL(tab.url).hostname : '')}
                    </p>
                  </div>
                  <ExternalLink className="w-3 h-3 text-khiimori-text-muted ml-auto flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
              ))}
            </div>
          </Card>
        </>
      )}
    </PageLayout>
  );
}